<template>
  <div class="app-parameters">
    <PageContent>
      <div v-if="domain">

        <section class="shadow sm:overflow-hidden sm:rounded-md">
          <div class="flex items-start justify-between bg-white">
            <!-- infos -->
            <div class="w-1/2">
              <div class="col-span-2 p-6 md:col-span-1 flex flex-col gap-3">
                <div class="">
                  <label for="domainLabel" class="block text-sm font-medium leading-6 text-gray-900">
                    Nom du compte
                  </label>
                  <input
                    type="text"
                    name="label"
                    id="domainLabel"
                    class="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-900 sm:text-sm sm:leading-6"
                    v-model="domain.label"
                  />
                </div>

                <div>
                  <label for="domainHostnameAdd" class="block text-sm font-medium leading-6 text-gray-900">
                    Noms de domaine autorisés pour l'inscription libre
                  </label>
                  <div class="mt-2 flex rounded-md shadow-sm">
                    <div class="relative flex flex-grow items-stretch focus-within:z-10">
                      <input
                        type="text"
                        id="domainHostnameAdd"
                        class="block w-full rounded-none rounded-l-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        placeholder="exemple.com"
                        v-model="pattern"
                        @keyup.enter="addHostname"
                      />
                    </div>
                    <button
                      type="button"
                      class="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset bg-primary-600 ring-primary-300 hover:bg-primary-700"
                      @click="addHostname"
                    >
                      Ajouter
                    </button>
                  </div>
                  <ul>
                    <li
                      v-for="(hostname, i) in domain.hostnames"
                      :key="i"
                      class="flex items-baseline gap-2"
                      >
                      <span @click="removeHostname(hostname.pattern)">
                        <i class="fas fa-times text-red-700 cursor-pointer" />
                      </span>
                      {{ hostname.pattern }}
                    </li>
                  </ul>
                </div>
                <div class="mt-4">
                  <label for="domainEmployeesCapacity" class="block text-sm font-medium leading-6 text-gray-900">
                    Effectif de référence
                  </label>
                  <input
                    type="number"
                    inputmode="numeric"
                    min="0"
                    step="1"
                    id="domainEmployeesCapacity"
                    class="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-900 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:text-gray-500"
                    :disabled="!canEditEmployeesCapacity"
                    :readonly="!canEditEmployeesCapacity"
                    :placeholder="!hasManualEmployeesCapacity ? formatCapacity(effectiveEmployeesCapacity) : ''"
                    v-model="employeesCapacityLocal"
                  />
                  <p class="mt-2 text-xs text-gray-500">
                    {{ employeesCapacityHelper }}
                  </p>
                </div>
                <div v-if="canEditLaunchDate" class="mt-4">
                  <label for="domainLaunchDate" class="block text-sm font-medium leading-6 text-gray-900">
                    Date de lancement
                  </label>
                  <t-datepicker
                    class="mt-2"
                    :value="launchDatePickerValue"
                    value-format="yyyy-MM-dd"
                    user-format="d/m/Y"
                    placeholder="jj/mm/aaaa"
                    :week-start="1"
                    clearable
                    @input="handleLaunchDateInput"
                  />
                  <p class="mt-2 text-xs text-gray-500">
                    Utilisée dans le tracking salariés. Laisser vide si la date n'est pas renseignée.
                  </p>
                </div>
              </div>
            </div>

            <!-- preview -->
            <div
              class="px-4 py-6 sm:p-6"
              style="width: 390px">
              <div
                class="flex justify-between items-center p-4 rounded-t-2xl"
                :style="'background-color:' + domain.color"
              >
                <div class="w-1/6 h-full grow-0 shrink-0">
                  <img
                    v-if="domain.logo"
                    class="h-full"
                    :src="storageUrl + '/logos/' + domain.logo"
                  />
                </div>
                <div class="flex-2 grow text-center w-auto text-white text-lg leading-5">
                  {{ domain.label }}
                </div>
                <div class="w-1/6 grow-0 shrink-0 flex text-right">
                </div>
              </div>
              <div class="text-center text-sm text-gray-400 py-2">
                Aperçu du rendu sur mobile
              </div>

              <div class="flex p-5 justify-between">
                <t-input-group label="Couleur">
                  <v-swatches v-model="domain.color"
                    popover-y="bottom"
                    shapes="circles"
                    swatches="text-advanced"
                  />
                </t-input-group>
                <t-input-group label="Logo">
                  <b-upload @input="addLogo" accept="image/png, image/jpeg">
                    <div
                      class="rounded-full p-1 w-10 text-center"
                      :style="'background-color:'+domain.color+';'"
                    >
                      <img
                        v-if="domain.logo"
                        :src="storageUrl + '/logos/' + domain.logo"
                        class="h-8 w-8 block m-auto"
                        alt=""
                      />
                      <b-image v-else class="is-64x64" :src="logoMVP" alt="" />
                    </div>
                  </b-upload>
                </t-input-group>
              </div>
            </div>
          </div>

          <!-- modules -->
          <div class="bg-white px-6 pb-6">
            <fieldset
              class="clos-span-3 md:col-span-1"
              v-if="['admin','community-manager'].some(role => roles.includes(role))"
            >
              <legend class="text-base font-medium text-gray-900">
                Modules
              </legend>

              <div class="mt-4 grid grid-cols-7">
                <div
                  class="flex"
                  v-for="mod in modules"
                  :key="mod.slug"
                >
                  <label class="flex items-center">
                    <t-checkbox
                      :id="'module-' + mod.slug"
                      type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      :checked="isModuleActivated(mod.slug)"
                      @input="$event => handleModule($event, mod.slug)"
                    />
                    <span class="ml-3 text-sm">
                      {{ mod.title }}
                    </span>
                  </label>
                </div>

              </div>

              <div
                v-if="isModuleActivated('chatbot_ai')"
                class="mt-5 max-w-2xl rounded-md border border-gray-200 bg-gray-50 p-4"
              >
                <p class="text-sm font-medium text-gray-900">
                  Accès à l’assistant IA
                </p>
                <p class="mt-1 text-xs text-gray-500">
                  Choisissez qui peut utiliser l’assistant depuis l’application mobile.
                </p>
                <div class="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-6">
                  <label class="flex cursor-pointer items-start gap-2">
                    <input
                      type="radio"
                      name="chatbot-ai-access-scope"
                      value="elected_only"
                      class="mt-1"
                      :checked="chatbotAiAccessScope() === 'elected_only'"
                      @change="handleChatbotAiAccessScope('elected_only')"
                    />
                    <span>
                      <span class="block text-sm font-medium text-gray-900">Élus uniquement</span>
                      <span class="block text-xs text-gray-500">Comportement actuel.</span>
                    </span>
                  </label>
                  <label class="flex cursor-pointer items-start gap-2">
                    <input
                      type="radio"
                      name="chatbot-ai-access-scope"
                      value="all_users"
                      class="mt-1"
                      :checked="chatbotAiAccessScope() === 'all_users'"
                      @change="handleChatbotAiAccessScope('all_users')"
                    />
                    <span>
                      <span class="block text-sm font-medium text-gray-900">Tous les utilisateurs</span>
                      <span class="block text-xs text-gray-500">Inclut les salariés ayant le rôle utilisateur.</span>
                    </span>
                  </label>
                </div>
              </div>
            </fieldset>
            <div class="px-4 pt-2 text-right sm:px-6">
              <button
                type="submit"
                class="inline-flex justify-center rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
                @click="save"
              >
                Sauvegarder
              </button>
            </div>
          </div>
          <div class="bg-white px-6 pb-6">
            <fieldset
              class="clos-span-3 md:col-span-1"
              v-if="['admin','community-manager'].some(role => roles.includes(role))"
            >
              <legend class="text-base font-medium text-gray-900">
                Notifications
              </legend>

              <div class="mt-4">
                <label class="flex items-center">
                  <t-toggle
                  id="can-avoid-notifications"
                  v-model="domain.can_avoid_notifications"
                  :value="true"
                  :uncheckedValue="false"
                  @change="save"
                  />
                  <span class="ml-3 text-sm">
                    Peut ne pas notifier
                  </span>
                </label>
              </div>
            </fieldset>
          </div>
        </section>
        <section
          class="mt-6 shadow sm:overflow-hidden sm:rounded-md p-4 bg-white"
          v-if="['admin','community-manager'].some(role => roles.includes(role))"
        >
          <h3 class="text-lg font-medium leading-6 text-gray-900 mb-3">
            Liens du domaine
          </h3>
              <div class="flex items-end gap-3 mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-900">
                    Label
                  </label>
                  <input
                    v-model="newLink.label"
                    type="text"
                    class="mt-1 block w-full border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-900">
                URL
                  </label>
                  <input
                    v-model="newLink.url"
                type="text"
                    class="mt-1 block w-full border-gray-300 rounded-md"
                  />
                </div>
                <button
                  class="bg-primary-600 text-white px-3 py-2 rounded-md hover:bg-primary-700"
                  @click="createLink"
                >
                  Ajouter
                </button>
              </div>
              <table class="w-full divide-y divide-gray-200 table-fixed">
                <thead>
                  <tr class="bg-gray-50">
                    <th scope="col" class="px-6 py-3 w-1/3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Label
                    </th>
                    <th scope="col" class="px-6 py-3 w-1/3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                    </th>
                    <th scope="col" class="px-6 py-3 w-1/3"></th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(link) in domainLinks" :key="link.id">
                <td
                  class="px-6 py-4 truncate overflow-hidden text-ellipsis whitespace-nowrap"
                  :title="link.label"
                >
                      <template v-if="editingLink === link.id">
                    <input
                      type="text"
                      v-model="link.label"
                      class="border rounded px-2 py-1 w-full"
                    />
                      </template>
                      <template v-else>
                        {{ link.label }}
                      </template>
                    </td>
                <td
                  class="px-6 py-4 truncate overflow-hidden text-ellipsis whitespace-nowrap"
                  :title="link.url"
                >
                      <template v-if="editingLink === link.id">
                    <input
                      type="text"
                      v-model="link.url"
                      class="border rounded px-2 py-1 w-full"
                    />
                      </template>
                      <template v-else>
                    {{ link.url }}
                      </template>
                    </td>
                    <td class="px-6 py-4 text-right">
                      <template v-if="editingLink === link.id">
                    <button
                      class="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 mr-2"
                      @click="updateLink(link)"
                    >
                      Sauvegarder
                    </button>
                    <button
                      class="bg-gray-200 text-gray-600 px-3 py-1 rounded hover:bg-gray-300"
                      @click="cancelEdit()"
                    >
                      Annuler
                    </button>
                      </template>
                      <template v-else>
                    <button
                      class="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 mr-2"
                      @click="editLink(link.id)"
                    >
                      Modifier
                    </button>
                    <button
                      class="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                      @click="deleteLink(link.id)"
                    >
                      Supprimer
                    </button>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
        </section>
         <section
          class="mt-6 shadow sm:overflow-hidden sm:rounded-md p-4 bg-white"
          v-if="['admin','community-manager','director'].some(role => roles.includes(role))"
        >
          <h3 class="text-lg font-medium leading-6 text-gray-900 mb-3">
            Utilisateurs bloqués (emails blacklistés)
          </h3>
          <div class="flex items-end gap-3 mb-4">
            <div>
              <label class="block text-sm font-medium text-gray-900">
                Email
              </label>
              <input
                v-model="newBlacklistedEmail.email"
                type="email"
                class="mt-1 block w-full border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-900">
                Raison (optionnelle)
              </label>
              <input
                v-model="newBlacklistedEmail.reason"
                type="text"
                class="mt-1 block w-full border-gray-300 rounded-md"
              />
            </div>
            <button
              class="bg-primary-600 text-white px-3 py-2 rounded-md hover:bg-primary-700"
              @click="createBlacklistedEmail"
            >
              Ajouter
            </button>
          </div>
          <table class="w-full divide-y divide-gray-200 table-fixed">
            <thead>
              <tr class="bg-gray-50">
                <th scope="col" class="px-6 py-3 w-1/3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" class="px-6 py-3 w-1/3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Raison
                </th>
                <th scope="col" class="px-6 py-3 w-1/3"></th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(email) in blacklistedEmails" :key="email.id">
                <td class="px-6 py-4 truncate overflow-hidden text-ellipsis whitespace-nowrap" :title="email.email">
                  <template v-if="editingBlacklistedEmail === email.id">
                    <input type="email" v-model="email.email" class="border rounded px-2 py-1 w-full" />
                  </template>
                  <template v-else>
                    {{ email.email }}
                  </template>
                </td>
                <td class="px-6 py-4 truncate overflow-hidden text-ellipsis whitespace-nowrap" :title="email.reason">
                  <template v-if="editingBlacklistedEmail === email.id">
                    <input type="text" v-model="email.reason" class="border rounded px-2 py-1 w-full" />
                  </template>
                  <template v-else>
                    {{ email.reason }}
                  </template>
                </td>
                <td class="px-6 py-4 text-right">
                  <template v-if="editingBlacklistedEmail === email.id">
                    <button class="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 mr-2" @click="updateBlacklistedEmail(email)">Sauvegarder</button>
                    <button class="bg-gray-200 text-gray-600 px-3 py-1 rounded hover:bg-gray-300" @click="cancelEditBlacklistedEmail()">Annuler</button>
                  </template>
                  <template v-else>
                    <button class="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 mr-2" @click="editBlacklistedEmail(email.id)">Modifier</button>
                    <button class="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700" @click="deleteBlacklistedEmail(email.id)">Supprimer</button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    </PageContent>
  </div>
</template>

<script>
// import VueTimepicker from 'vue2-timepicker'
// import 'vue2-timepicker/dist/VueTimepicker.css'
import { http } from '@/services/api.service'
import { mapGetters } from 'vuex'
import VSwatches from 'vue-swatches'
import PageContent from '@/components/PageContent.vue'
import 'vue-swatches/dist/vue-swatches.css'

export default {
  name: 'app-parameters',
  components: {
    PageContent,
    VSwatches // VueTimepicker,
  },
  computed: {
    ...mapGetters([
      'roles',
      'user'
    ]),
    domain: {
      get () {
        return this.$store.getters.domain
      },
      set (domain) {
        return domain
      }
    },
    canEditEmployeesCapacity () {
      const hostnames = this.domain?.hostnames
      return Array.isArray(hostnames) && hostnames.length > 0
    },
    effectiveEmployeesCapacity () {
      return this.normalizeEmployeesCapacity(
        this.domain?.effective_employees_capacity ?? this.domain?.effectiveEmployeesCapacity
      )
    },
    hasManualEmployeesCapacity () {
      return Boolean(this.domain?.employees_capacity_is_manual)
    },
    canEditLaunchDate () {
      return ['admin', 'community-manager'].some(role => this.roles.includes(role))
    },
    launchDatePickerValue () {
      return this.toDate(this.domain?.launch_date)
    },
    employeesCapacityHelper () {
      if (!this.canEditEmployeesCapacity) {
        return 'Valeur calculée automatiquement.'
      }

      if (this.hasManualEmployeesCapacity) {
        return 'Valeur renseignée manuellement pour contrôler les inscriptions libres.'
      }

      const auto = this.effectiveEmployeesCapacity
      if (auto !== null) {
        return `Sans saisie, l’effectif est calculé automatiquement (${this.formatCapacity(auto)} collaborateurs).`
      }
      return 'Sans saisie, l’effectif est calculé automatiquement.'
    }
  },
  data () {
    return {
      logoUrl: null,
      logoMVP: '/logo.png',
      pattern: '',
      employeesCapacityLocal: '',
      isEmployeesCapacityManualLocal: false,
      defaultColor: '#2E6FFB',
      modules: [
        {
          slug: 'faq',
          title: 'Foire aux questions'
        },
        {
          slug: 'chat',
          title: 'Messagerie'
        },
        {
          slug: 'news',
          title: 'Actualités'
        },
        // {
        //   slug: 'payment',
        //   title: 'Paiement'
        // },
        {
          slug: 'survey',
          title: 'Sondages'
        },
        {
          slug: 'document',
          title: 'Documents'
        },
        // {
        //   slug: 'contact',
        //   title: 'Contact'
        // },
        {
          slug: 'chatbot_ai',
          title: 'Assistant IA'
        }
      ],
      collapsed: {
        env: true,
        modules: true,
        management: true,
        chat: false,
        contact: false,
        faq: false
      },
      fileLogo: null,
      domainLinks: [],
      newLink: {
        label: '',
        url: ''
      },
      editingLink: null,
      blacklistedEmails: [],
      newBlacklistedEmail: {
        email: '',
        reason: ''
      },
      editingBlacklistedEmail: null
    }
  },
  watch: {
    domain: {
      handler (domain) {
        this.syncEmployeesCapacity(domain)
      },
      immediate: true
    },
    canEditEmployeesCapacity () {
      this.syncEmployeesCapacity(this.domain)
    },
    employeesCapacityLocal (value) {
      if (!this.domain) {
        return
      }
      if (!this.canEditEmployeesCapacity) {
        this.domain.employees_capacity = null
        this.domain.employees_capacity_is_manual = false
        this.isEmployeesCapacityManualLocal = false
        return
      }

      const numeric = this.normalizeEmployeesCapacity(value)
      if (numeric !== null) {
        this.isEmployeesCapacityManualLocal = true
        this.domain.employees_capacity = numeric
        this.domain.employees_capacity_is_manual = true
      } else {
        this.isEmployeesCapacityManualLocal = false
        this.domain.employees_capacity = null
        this.domain.employees_capacity_is_manual = false
      }
    }
  },
  created () {
    this.storageUrl = process.env.VUE_APP_STORAGE_URL
    if (this.domain.has_logo) {
      this.logoUrl = `${this.storageUrl}/logo/${this.domain.logo}`
    }
    if (!this.domain.color) {
      this.domain.color = this.defaultColor
    }
    this.fetchDomainLinks()
    this.fetchBlacklistedEmails()
  },
  methods: {
    normalizeEmployeesCapacity (value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const numeric = Number(value)
      return Number.isFinite(numeric) && numeric >= 0 ? numeric : null
    },
    syncEmployeesCapacity (domain) {
      if (!domain) {
        this.isEmployeesCapacityManualLocal = false
        this.employeesCapacityLocal = ''
        return
      }

      const isManual = Boolean(domain.employees_capacity_is_manual && this.canEditEmployeesCapacity)
      this.isEmployeesCapacityManualLocal = isManual
      domain.employees_capacity_is_manual = isManual

      if (isManual) {
        const manualValue = this.normalizeEmployeesCapacity(domain.employees_capacity)
        const formatted = manualValue !== null ? String(manualValue) : ''
        if (this.employeesCapacityLocal !== formatted) {
          this.employeesCapacityLocal = formatted
        }
      } else {
        if (domain.employees_capacity !== null) {
          domain.employees_capacity = null
        }
        if (this.employeesCapacityLocal !== '') {
          this.employeesCapacityLocal = ''
        }
      }
    },
    formatCapacity (value) {
      const numeric = this.normalizeEmployeesCapacity(value)
      if (numeric === null) {
        return '0'
      }
      try {
        return new Intl.NumberFormat('fr-FR').format(numeric)
      } catch (error) {
        return String(numeric)
      }
    },
    handleLaunchDateInput (value) {
      if (!this.domain) {
        return
      }

      this.domain.launch_date = this.normalizeLaunchDate(value)
    },
    normalizeLaunchDate (value) {
      if (!value) {
        return null
      }

      if (value instanceof Date) {
        return this.toIsoDate(value)
      }

      if (typeof value === 'string') {
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (isoMatch) {
          const [, year, month, day] = isoMatch
          return this.toIsoDate(new Date(Number(year), Number(month) - 1, Number(day)))
        }

        const frenchMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
        if (frenchMatch) {
          const [, day, month, year] = frenchMatch
          return this.toIsoDate(new Date(Number(year), Number(month) - 1, Number(day)))
        }

        return this.toIsoDate(new Date(value))
      }

      return null
    },
    toIsoDate (date) {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return null
      }

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },
    toDate (value) {
      if (!value) {
        return null
      }

      if (value instanceof Date) {
        return value
      }

      if (typeof value === 'string') {
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (isoMatch) {
          const [, year, month, day] = isoMatch
          const date = new Date(Number(year), Number(month) - 1, Number(day))
          return isNaN(date.getTime()) ? null : date
        }

        const frenchMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
        if (frenchMatch) {
          const [, day, month, year] = frenchMatch
          const date = new Date(Number(year), Number(month) - 1, Number(day))
          return isNaN(date.getTime()) ? null : date
        }
      }

      const parsed = new Date(value)
      return isNaN(parsed.getTime()) ? null : parsed
    },
    ensureModule (slug) {
      if (!this.domain[slug]) {
        this.$set(this.domain, slug, { is_activated: false })
      }

      return this.domain[slug]
    },
    isModuleActivated (slug) {
      return Boolean(this.domain?.[slug]?.is_activated)
    },
    chatbotAiAccessScope () {
      const scope = this.domain?.chatbot_ai?.access_scope
      return ['elected_only', 'all_users'].includes(scope) ? scope : 'elected_only'
    },
    handleModule (checked, slug) {
      this.ensureModule(slug).is_activated = checked
      this.save()
    },
    handleChatbotAiAccessScope (scope) {
      if (!['elected_only', 'all_users'].includes(scope)) {
        return
      }

      this.$set(this.ensureModule('chatbot_ai'), 'access_scope', scope)
      this.save()
    },
    handleCanAvoidNotifications (checked) {
      this.domain.can_avoid_notifications = checked
      this.save()
    },
    save () {
      http.put(`/domain/${this.domain.uuid}`, this.buildDomainPayload()).then(({ data }) => {
        this.$store.commit('SET_DOMAIN', data)
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Paramètres mis à jour avec succès',
          position: 'is-top-right',
          type: 'is-success',
          hasIcon: true
        })
      }).catch(err => {
        console.log(err)
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Nos serveurs rencontrent des difficultés à mettre à jour ce domaine',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    },
    buildDomainPayload () {
      const payload = {
        ...this.domain,
        chat: {
          ...this.domain.chat
        }
      }

      delete payload.chat.interlocutor_note_text
      delete payload.chat.cse_note_text
      delete payload.chat.maviepro_note_text
      delete payload.chat.cse_auto_reply_text
      delete payload.chat.maviepro_auto_reply_text

      if (!this.canEditLaunchDate) {
        delete payload.launch_date
      } else {
        payload.launch_date = this.normalizeLaunchDate(payload.launch_date)
      }

      return payload
    },
    deleteDomain () {
      this.$buefy.dialog.confirm({
        title: 'Suppression',
        message: `Êtes vous sur de vouloir supprimer ce domaine ?`,
        cancelText: 'Annuler',
        confirmText: 'Confirmer',
        type: 'is-danger',
        onConfirm: () => {
          http.delete(`/domain/${this.domain.uuid}`).then(res => {
            this.$buefy.notification.open({
              duration: 3000,
              message: 'Le domaine a été supprimé avec succès !',
              position: 'is-bottom-left',
              type: 'is-success',
              hasIcon: true
            })
            this.$emit('removeDomain', this.domain)
          }).catch(err => {
            console.log(err)
            this.$buefy.notification.open({
              duration: 3000,
              message: 'Nos serveurs rencontrent des difficultés à supprimer ce domaine',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          })
        }
      })
    },
    addHostname () {
      http.post(`domain/${this.domain.uuid}/hostname`, { pattern: this.pattern }).then(res => {
        this.domain.hostnames = res.data
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.pattern = ''
      })
    },
    removeHostname (pattern) {
      http.delete(`domain/${this.domain.uuid}/hostname`, { data: { pattern } }).then(res => {
        this.domain.hostnames = res.data
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.pattern = ''
      })
    },
    // loadUserDomain () {
    //   http.get('/me/domain').then(res => {
    //     this.settings.name = res.data.label
    //     this.settings.messages = res.data.messages
    //     this.settings.timetable = res.data.timetable
    //   })
    // },
    updateParameters () {
      http.put(`/domain/${this.domain.uuid}/settings`, { ...this.settings, name: this.domain.label })
        .then(res => {
          this.$buefy.notification.open({
            duration: 3000,
            message: 'Paramètres sauvegardés !',
            position: 'is-bottom-left',
            type: 'is-success',
            hasIcon: true
          })
          this.domain = res.data
        })
        .catch(err => {
          console.log(err)
          this.$buefy.notification.open({
            duration: 3500,
            message: 'Nos serveurs rencontrent des difficultés à mettre à jour ce domaine',
            position: 'is-bottom-left',
            type: 'is-warning',
            hasIcon: true,
            icon: 'server'
          })
        })
    },
    addLogo (file) {
      const formData = new FormData()
      formData.append('logo', file)
      http.post(`/domain/${this.domain.uuid}/logo`, formData).then(({ data }) => {
        this.domain.logo = data.logo
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Le logo du domaine a bien été uploadé',
          position: 'is-bottom-left',
          type: 'is-success',
          hasIcon: true
        })
      }).catch(err => {
        console.log(err)
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Nos serveurs rencontrent des difficultés à mettre à jour le logo du domaine',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    },
    fetchDomainLinks () {
      http.get('/domain/links') // This sets the "Domain" header automatically if your axios setup does so
        .then(({ data }) => {
          this.domainLinks = data
        })
        .catch(err => {
          console.log(err)
        })
    },
    // CREATE a new link
    createLink () {
      if (!this.newLink.label || !this.newLink.url) {
        this.$buefy.notification.open({
          message: 'Veuillez renseigner le label et le lien',
          type: 'is-warning'
        })
        return
      }

      http.post('/domain/links', {
        label: this.newLink.label,
        url: this.newLink.url
      }).then(({ data }) => {
        // Clear the form
        this.newLink.label = ''
        this.newLink.url = ''
        // Option 1: Just refresh the entire list
        this.fetchDomainLinks()

        // Option 2: Or push the newly created link to domainLinks array:
        // this.domainLinks.push(data);

        this.$buefy.notification.open({
          message: 'Lien créé avec succès',
          type: 'is-success'
        })
      }).catch(err => {
        console.log(err)
        this.$buefy.notification.open({
          message: 'Erreur lors de la création du lien',
          type: 'is-danger'
        })
      })
    },

    // Switch to "edit mode" for a given link ID
    editLink (id) {
      this.editingLink = id
    },

    // Cancel editing
    cancelEdit () {
      // Refresh the list from server to discard unsaved changes, or you can revert changes from a copy
      this.fetchDomainLinks()
      this.editingLink = null
    },

    // UPDATE a link
    updateLink (link) {
      http.put(`/domain/links/${link.id}`, {
        label: link.label,
        url: link.url
        // domain_id is handled automatically in your controller
      }).then(({ data }) => {
        this.editingLink = null
        this.$buefy.notification.open({
          message: 'Lien mis à jour avec succès',
          type: 'is-success'
        })
      }).catch(err => {
        console.log(err)
        this.$buefy.notification.open({
          message: 'Erreur lors de la mise à jour du lien',
          type: 'is-danger'
        })
      })
    },

    // DELETE a link
    deleteLink (id) {
      this.$buefy.dialog.confirm({
        title: 'Confirmation',
        message: 'Voulez-vous vraiment supprimer ce lien ?',
        confirmText: 'Supprimer',
        cancelText: 'Annuler',
        type: 'is-danger',
        onConfirm: () => {
          http.delete(`/domain/links/${id}`)
            .then(() => {
              // Remove link from array or just refetch
              // Option 1: Filter out from local array
              this.domainLinks = this.domainLinks.filter(link => link.id !== id)
              // Option 2: Or call this.fetchDomainLinks()
              this.$buefy.notification.open({
                message: 'Lien supprimé avec succès',
                type: 'is-success'
              })
            })
            .catch(err => {
              console.log(err)
              this.$buefy.notification.open({
                message: 'Erreur lors de la suppression du lien',
                type: 'is-danger'
              })
            })
        }
      })
    },
    fetchBlacklistedEmails () {
      http.get('/blacklisted-emails').then(({ data }) => {
        this.blacklistedEmails = data
      }).catch(err => {
        console.log(err)
      })
    },
    createBlacklistedEmail () {
      if (!this.newBlacklistedEmail.email) {
        this.$buefy.notification.open({
          message: 'Veuillez renseigner un email',
          type: 'is-warning'
        })
        return
      }
      http.post('/blacklisted-emails', this.newBlacklistedEmail).then(({ data }) => {
        this.newBlacklistedEmail.email = ''
        this.newBlacklistedEmail.reason = ''
        this.fetchBlacklistedEmails()
        this.$buefy.notification.open({
          message: 'Email bloqué avec succès',
          type: 'is-success'
        })
      }).catch(err => {
        console.log(err)
        this.$buefy.notification.open({
          message: 'Erreur lors du blocage de l\'email',
          type: 'is-danger'
        })
      })
    },
    editBlacklistedEmail (id) {
      this.editingBlacklistedEmail = id
    },
    cancelEditBlacklistedEmail () {
      this.fetchBlacklistedEmails()
      this.editingBlacklistedEmail = null
    },
    updateBlacklistedEmail (email) {
      http.put(`/blacklisted-emails/${email.id}`, {
        email: email.email,
        reason: email.reason
      }).then(({ data }) => {
        this.editingBlacklistedEmail = null
        this.$buefy.notification.open({
          message: 'Email mis à jour avec succès',
          type: 'is-success'
        })
        this.fetchBlacklistedEmails()
      }).catch(err => {
        console.log(err)
        this.$buefy.notification.open({
          message: 'Erreur lors de la mise à jour',
          type: 'is-danger'
        })
      })
    },
    deleteBlacklistedEmail (id) {
      this.$buefy.dialog.confirm({
        title: 'Confirmation',
        message: 'Voulez-vous vraiment supprimer ce blocage ?',
        confirmText: 'Supprimer',
        cancelText: 'Annuler',
        type: 'is-danger',
        onConfirm: () => {
          http.delete(`/blacklisted-emails/${id}`)
            .then(() => {
              this.blacklistedEmails = this.blacklistedEmails.filter(e => e.id !== id)
              this.$buefy.notification.open({
                message: 'Blocage supprimé avec succès',
                type: 'is-success'
              })
            })
            .catch(err => {
              console.log(err)
              this.$buefy.notification.open({
                message: 'Erreur lors de la suppression',
                type: 'is-danger'
              })
            })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.settings-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 4px;
}

.module {
  margin-top: 20px;
}
.planning {
  margin-top: 20px;

  li {
    margin: 10px;
    display: flex;
  }
}
.fixed-hero {
  padding-top: 150px;
  .hero {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 99;
    margin: 0 260px;
  }
}
.column-timetable {
  display: flex;
  align-items: center;

  .timetable-label {
    flex-grow: 1;
  }

  .b-clockpicker {
    width: 90px;
  }
}

.preview {
  padding: 0 1rem;
  position: relative;
  .label {
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
    font-weight: normal;
    font-size: .9rem;
    padding-top: 4px;
  }
  .preview-action {
    position: absolute;
    right: 1rem;
    top: -1rem;
    background: #fff;
    padding-left: 5px;
    padding-right: 5px;
    border: 1px solid #ccc;
    cursor: pointer;
    border-radius: 5px;
    &:hover {
      box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.3);
    }
  }
  h3 {
    font-size: 18px;
    text-align: center;
  }
  .header {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 2rem;
    color: #fff;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  }
  .preview-right {
    width: 64px;
  }
}

.collapser {
  cursor: pointer;

  h3 {
    margin: 20px 0 10px 0;
  }
}
</style>
